<template>
  <div class="delivery-modal">
    <div class="delivery-modal__grid">
      <div class="delivery-modal__panel">
        <!-- Контент вкладок (селектор табов вынесен на карту, см. .map-tabs) -->
        <div v-show="selectedTabIndex === 0" class="delivery-tab">
          <div class="delivery-tab__scroll">
            <!-- Режим доставки - показываем адреса и зоны -->
            <div class="delivery-modal__address">
              <!-- brand-font: заголовок окна выбора зоны доставки — одно из мест
                   фирменного шрифта заведения (см. «Внешний вид») -->
              <div class="mb-3 text-[18px] font-semibold brand-font">Укажите адрес доставки</div>
              <UiKitInput
                v-model="deliveryAddressInput"
                size="lg"
                placeholder="Введите адрес доставки"
                :invalid="!!deliveryAddressErrorMsg"
                :error-message="deliveryAddressErrorMsg"
                @focusout="onManualAddressEnter"
                @keydown.enter="onManualAddressEnter"
              />
              <small v-if="missingAddressParts.length" class="text-orange-500 dark:text-orange-400 block mt-1">
                Передвиньте маркер на конкретное здание — нужны {{ missingAddressPartsLabel }}
              </small>
            </div>

            <!-- Плашка с предложением адреса по геолокации -->
            <div v-if="geoSuggestion" class="geo-suggest">
              <div class="geo-suggest__title">
                <UIcon name="i-heroicons-map-pin" class="h-5 w-5 text-orange-500" />
                <span>Это ваш адрес?</span>
              </div>
              <div class="geo-suggest__addr">{{ geoSuggestion.address }}</div>
              <div v-if="!geoSuggestion.inZone" class="geo-suggest__warn">
                Вне зоны доставки — можно подставить и переместить маркер
              </div>
              <div class="geo-suggest__actions">
                <UiButtonMain
                  :label="geoSuggestion.inZone ? 'Да, сохранить' : 'Использовать'"
                  color="red"
                  size="sm"
                  @click="acceptGeoSuggestion"
                />
                <button type="button" class="geo-suggest__no" @click="rejectGeoSuggestion">
                  Нет, выберу сам
                </button>
              </div>
            </div>

            <!-- Раскрывающаяся секция с деталями адреса -->
            <div class="address-details" :class="{ 'address-details--open': showExtraDetails }">
              <button
                type="button"
                class="address-details__toggle"
                :aria-expanded="showExtraDetails"
                @click="showExtraDetails = !showExtraDetails"
              >
                <span class="address-details__toggle-icon">
                  <UIcon name="i-heroicons-adjustments-horizontal" class="h-4 w-4" />
                </span>
                <span class="address-details__toggle-label">Уточнить детали</span>
                <span v-if="extraDetailsSummary" class="address-details__summary">{{ extraDetailsSummary }}</span>
                <UIcon
                  name="i-heroicons-chevron-down"
                  class="address-details__chevron h-4 w-4"
                />
              </button>

              <Transition name="address-details-expand">
                <div v-if="showExtraDetails" class="address-details__grid">
                  <UiKitInput v-model="extraDetails.apartment" label="Квартира" placeholder="42" />
                  <UiKitInput v-model="extraDetails.entrance" label="Подъезд" placeholder="3" />
                  <UiKitInput v-model="extraDetails.floor" type="number" label="Этаж" placeholder="5" />
                  <UiKitInput v-model="extraDetails.intercom" label="Код домофона" placeholder="1234#" />
                  <div class="col-span-2">
                    <UiKitTextarea
                      v-model="extraDetails.comment"
                      label="Комментарий курьеру"
                      :rows="2"
                      placeholder="Например: позвонить за 10 минут"
                    />
                  </div>
                </div>
              </Transition>
            </div>

            <div v-if="userAddresses.length" class="delivery-tab__addresses flex flex-col gap-4 my-4">
              <label class="block font-semibold mb-1.5 lg:flex-shrink-0">Мои адреса</label>
              <div
                v-for="(item, idx) in userAddresses"
                :key="item.id || idx"
                class="flex items-center gap-4 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <input
                  :id="'address-' + (item.id || idx)"
                  type="radio"
                  :value="item.id"
                  :name="'address-radio'"
                  class="form-radio h-5 w-5 accent-red-600 dark:accent-red-500"
                  :checked="form.delivery_address?.address === item.address"
                  @change="onSelectAddress(item)"
                />
                <label :for="'address-' + (item.id || idx)" class="cursor-pointer text-base w-full">
                  <span class="block font-medium">{{ item.address }}</span>
                  <span class="text-sm text-gray-600 dark:text-gray-400 flex flex-wrap gap-2 mt-1">
                    <span v-if="item.zona">Зона: {{ item.zona }}</span>
                    <span v-if="item.apartment">• Кв. {{ item.apartment }}</span>
                    <span v-if="item.entrance">• Подъезд {{ item.entrance }}</span>
                    <span v-if="item.floor">• Этаж {{ item.floor }}</span>
                  </span>
                  <span v-if="item.isDefault" class="block text-xs text-red-600 dark:text-red-400 mt-1 font-semibold">
                    ⭐ Адрес по умолчанию
                  </span>
                </label>
              </div>
            </div>

          </div>

          <UiButtonMain
            :disabled="isDeliverySaveDisabled"
            label="Сохранить"
            color="red"
            size="lg"
            block
            class="delivery-tab__save"
            @click="submit"
          />
        </div>

        <!-- Самовывоз -->
        <div v-show="selectedTabIndex === 1">
          <BranchSelectionList
            :branches="activeBranches as any"
            :selected-branch-id="selectedBranch?.id"
            title="Выберите ресторан для самовывоза"
            submit-label="Выбрать для самовывоза"
            radio-prefix="branch-takeaway"
            @select="onSelectBranch"
            @submit="submit"
          />
        </div>

        <!-- Ем в зале -->
        <div v-show="selectedTabIndex === 2">
          <BranchSelectionList
            :branches="activeBranches as any"
            :selected-branch-id="selectedBranch?.id"
            title="Выберите ресторан"
            submit-label="Выбрать ресторан"
            radio-prefix="branch-dine"
            @select="onSelectBranch"
            @submit="submit"
          />
        </div>
      </div>
      <ClientOnly>
        <div class="map-wrapper">
          <!-- Селектор способа получения — поверх карты, слева от крестика (дизайн как в корзине) -->
          <UiKitTabs
            class="map-tabs"
            :items="kitTabs"
            :model-value="selectedTabIndex"
            :panel="false"
            variant="solid"
            color="default"
            size="lg"
            full-width
            @update:model-value="onTabChange"
          />
          <div ref="ymap" class="delivery-modal__map"></div>
          <!-- Фиксированный маркер в центре карты -->
          <div v-if="selectedTabIndex === 0" class="center-marker">
            <img src="/img/marker.webp" alt="marker" />
          </div>
        </div>
        <template #fallback>
          <div class="map-wrapper">
            <div class="delivery-modal__map flex items-center justify-center bg-gray-100 dark:bg-gray-800">
              <div class="text-center p-4">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                <p class="text-gray-600 dark:text-gray-400">Загрузка карты...</p>
              </div>
            </div>
          </div>
        </template>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { consola } from 'consola';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { useAddressPointSync } from '../model/useAddressPointSync';
import { isHouseLevelResult } from '../lib/geocoderPrecision';
import { shouldPersistAddressPoint } from '../lib/persistPoint';
import { isSameStreetAndLocality, type AddressPlace } from '../lib/addressIdentity';
import { useCheckoutFormStore } from '~/features/basket/@x/map';
import {
  useAppearance,
  useInstitutionStore,
  apiInstitutions,
  findBranchAtPoint,
  isAtBranchPoint,
} from '@entities/institution';
import { DEFAULT_APPEARANCE } from '@shared/config';
import { useModalStore } from '@shared/model';
import { useUserProfileStore, canonicalSavedAddress } from '@entities/user';
import { useCartStore } from '@entities/order';
import { useAddresses } from '~/features/profile/@x/map';
import { useDarkMode, ensureYandexMaps, formatPointAddress } from '@shared/lib';
import type { CreateAddressPayload, UpdateAddressPayload } from '@entities/user';
import BranchSelectionList from '~/widgets/branch-selection-list/ui/BranchSelectionList.vue';

/* ---------- STORES ---------- */
const emit = defineEmits(['update:size']);

/* ---------- КОНСТАНТЫ ---------- */
const MAP_CONFIG = {
  DEFAULT_ZOOM: 16,
  BRANCH_ZOOM: 16,
  ADDRESS_ZOOM: 16,
  DEBOUNCE_DELAY: 100,
  COORDINATE_THRESHOLD: 0.001, // ~100м
} as const;

const MARKER_STYLES = {
  SELECTED: {
    borderColor: '#27ae60',
    borderWidth: '4px',
    scale: 'scale(1.2)',
  },
  DEFAULT: {
    borderColor: '#e74c3c',
    borderWidth: '3px',
    scale: 'scale(1)',
  },
} as const;

const checkoutStore = useCheckoutFormStore();
const setField = checkoutStore.setField;
const form = checkoutStore.OrderForm;
const cartStore = useCartStore();

// Строка адреса и точка, по которой определены зона и координаты, — см. useAddressPointSync.
/**
 * Строка адреса в поле окна — собственный черновик окна.
 *
 * Раньше поле читало адрес прямо из формы оформления, а форму перезаписывает вотчер на
 * выбранном адресе. Стоило чему-то снаружи подставить другой адрес (например
 * `ensureDefaultDeliveryAddress` подставляет адрес по умолчанию, когда у выбранного
 * пустая зона), и в поле молча появлялся ЧУЖОЙ адрес, а маркер оставался на введённом.
 * Клиент мог этого не заметить и отправить заказ не туда. Теперь поле принадлежит окну:
 * его меняют только сам клиент и `writeAddress` (маркер, выбор из «Моих адресов»).
 */
const addressDraft = ref(checkoutStore.OrderForm.delivery_address?.address ?? '');

// Подтверждение точки сверяется с тем, что клиент ВИДИТ в поле, а не с формой оформления.
const addressPoint = useAddressPointSync(() => addressDraft.value);
const { addressNotFound, isAddressUnconfirmed } = addressPoint;
const isSubmitting = ref(false);
/**
 * Маркер стоит на координатах филиала. Это стартовая точка карты у клиента без адресов,
 * а не его дом: такую точку показываем, но адресом доставки не делаем (заказ #8300 —
 * координаты филиала уехали в заказ вместе со строкой хутора вне зон).
 */
const pointAtBranch = ref<{ name: string } | null>(null);

// form.delivery_address может быть null (адрес ещё не выбран). Шаблон обращался к нему
// через non-null `!`, из-за чего рендер падал и карта вообще не монтировалась.
// Безопасная writable-модель для инпута адреса.
const deliveryAddressInput = computed<string>({
  get: () => addressDraft.value,
  set: (address: string) => {
    addressDraft.value = address;
    addressPoint.onAddressTyped();
    setField('delivery_address', { ...form.delivery_address, address });
  },
});

const institutionStore = useInstitutionStore();
const deliveryZones = computed(() => (institutionStore.institution as any)?.delivery_zones || []);

const { appearance } = useAppearance();

/**
 * Заливка зоны доставки.
 *
 * Если заведение задало свой цвет в разделе «Внешний вид» — красим им все зоны, как это
 * делают конструкторы витрин: гость видит одну фирменную зону. Пока цвет оставлен
 * стандартным, работает прежняя палитра — разные зоны это разные тарифы, и их полезно
 * различать. В фирменном режиме соседние зоны разводим прозрачностью, иначе границы сливаются.
 */
const ZONE_PALETTE = ['#44aaff55', '#ffaa4455', '#4caf5055', '#012e5355', '#bb44ff55'];

const zoneFillStyle = (index: number) => {
  const configured = appearance.value.deliveryZoneColor;
  if (configured === DEFAULT_APPEARANCE.deliveryZoneColor) {
    return { fillColor: ZONE_PALETTE[index % ZONE_PALETTE.length], fillOpacity: 0.25 };
  }
  return { fillColor: configured, fillOpacity: Math.min(0.45, 0.18 + index * 0.07) };
};

const modalStore = useModalStore();
const profileStore = useUserProfileStore();
const { setSelectedDeliveryAddress } = profileStore;

// Composable для работы с адресами (только для создания/обновления)
const { createAddress, updateAddress, hasAddresses } = useAddresses();

// Composable для работы с темной темой
const { isDark } = useDarkMode();

// Определяем режим работы модального окна
const mode = computed(() => modalStore.modalData?.mode || 'checkout'); // 'create', 'edit', 'checkout'
const editingAddress = computed(() => modalStore.modalData?.address || null);

// Определяем тип заказа: приоритет у переданного через modalData, затем из формы
const orderType = computed(() => {
  const passedType = modalStore.modalData?.orderType;
  if (passedType && ['delivery', 'takeaway', 'dine_in'].includes(passedType)) {
    return passedType;
  }
  return form.method_receipt || 'delivery';
});

// Локальное состояние для переключения табов в модальном окне
const localOrderType = ref(orderType.value);

// Определяем, нужно ли показывать функционал доставки
const isDeliveryMode = computed(() => localOrderType.value === 'delivery');
const isPickupOrDineInMode = computed(() => localOrderType.value === 'takeaway' || localOrderType.value === 'dine_in');

// Табы для переключения типов получения
const tabs = [
  { label: 'Доставка', value: 'delivery', slot: 'delivery' },
  { label: 'С собой', value: 'takeaway', slot: 'takeaway' },
  { label: 'Ем в зале', value: 'dine_in', slot: 'dine_in' },
];

// Items для UiKitTabs (ключ = индекс, чтобы не менять числовую модель selectedTabIndex)
const kitTabs = computed(() => tabs.map((tab, index) => ({ key: index, label: tab.label })));

const selectedTabIndex = ref(tabs.findIndex((tab) => tab.value === localOrderType.value));

// Обновляем selectedTabIndex при изменении orderType (например, при открытии модалки)
watch(
  orderType,
  (newType) => {
    if (newType && newType !== localOrderType.value) {
      localOrderType.value = newType;
      const newIndex = tabs.findIndex((tab) => tab.value === newType);
      if (newIndex >= 0) {
        selectedTabIndex.value = newIndex;
      }
    }
  },
  { immediate: true },
);

// Обработчик смены таба
function onTabChange(newIndex: number) {
  if (newIndex >= 0 && newIndex < tabs.length) {
    const newType = tabs[newIndex].value as 'delivery' | 'takeaway' | 'dine_in';
    consola.log('🔄 onTabChange в модалке: переключение на таб:', newType, 'индекс:', newIndex);

    localOrderType.value = newType;
    selectedTabIndex.value = newIndex;

    // ✅ ВАЖНО: Обновляем форму - это синхронизирует с MethodReceipt
    setField('method_receipt', newType);
    consola.log('✅ Тип заказа обновлен в store:', newType);

    // Перерисовываем карту при смене типа заказа
    if (map && window.ymaps) {
      consola.log('✅ onTabChange: вызываем updateMapForOrderType');
      updateMapForOrderType();
    } else {
      consola.warn('⚠️ onTabChange: карта еще не готова', { map: !!map, ymaps: !!window.ymaps });
    }
  }
}

// Функция для обновления карты в зависимости от типа заказа
// eslint-disable-next-line sonarjs/cognitive-complexity -- легаси-оркестрация Яндекс-карты; безопасный рефактор требует прогона карты вживую
function updateMapForOrderType() {
  if (!map || !window.ymaps) {
    consola.warn('⚠️ updateMapForOrderType: карта не инициализирована или ymaps не готов');
    return;
  }

  consola.log('🔄 updateMapForOrderType: обновляем карту для типа:', localOrderType.value);

  try {
    // Очищаем все полигоны и маркер адреса
    polygons.forEach((poly) => {
      try {
        if (poly && map.geoObjects) {
          map.geoObjects.remove(poly);
        }
      } catch (error) {
        consola.warn('⚠️ Ошибка при удалении полигона:', error);
      }
    });
    polygons = [];

    if (marker) {
      try {
        if (map.geoObjects) {
          map.geoObjects.remove(marker);
        }
      } catch (error) {
        consola.warn('⚠️ Ошибка при удалении маркера:', error);
      }
      marker = null;
    }

    // Очищаем маркеры филиалов
    branchMarkers.forEach((m) => {
      try {
        if (m && map.geoObjects) {
          map.geoObjects.remove(m);
        }
      } catch (error) {
        consola.warn('⚠️ Ошибка при удалении маркера филиала:', error);
      }
    });
    branchMarkers = [];

    // Удаляем старые обработчики событий карты
    try {
      if (map.events) {
        map.events.removeAll();
      }
    } catch (error) {
      consola.warn('⚠️ Ошибка при удалении обработчиков:', error);
    }
  } catch (error) {
    consola.error('❌ Ошибка в updateMapForOrderType при очистке:', error);
    return;
  }

  if (isDeliveryMode.value) {
    consola.log('📦 Режим доставки: добавляем зоны и маркер адреса');
    // Добавляем зоны доставки
    polygons = deliveryZones.value.map((z: any, i: number) => {
      const poly = new window.ymaps.Polygon(
        [toYaCoords(z.zone_coords)],
        {},
        {
          ...zoneFillStyle(i),
          strokeColor: '#222',
          strokeWidth: 2,
        },
      );
      map.geoObjects.add(poly);
      return poly;
    });

    // Определяем координаты для маркера адреса
    // Приоритет: ВСЕГДА показываем дефолтный адрес (если он есть)
    let initialCoords = selectedCoords.value;

    if (defaultAddress.value?.latitude && defaultAddress.value?.longitude) {
      initialCoords = [defaultAddress.value.latitude, defaultAddress.value.longitude];
      consola.log(
        '⭐ updateMapForOrderType: Используем координаты дефолтного адреса:',
        initialCoords,
        defaultAddress.value.address,
      );
    }
    // Если у пользователя НЕТ адресов вообще - показываем первый филиал
    else if (userAddresses.value.length === 0 && activeBranches.value.length > 0) {
      const firstBranch = activeBranches.value[0];
      if (firstBranch.latitude && firstBranch.longitude) {
        initialCoords = extractCoordinates(firstBranch as any);
        consola.log(
          '🏪 updateMapForOrderType: У пользователя нет адресов, показываем первый филиал:',
          initialCoords,
          firstBranch.name,
        );
      }
    }
    // Только если нет дефолтного адреса, используем другие варианты
    else if (fullAddressData.value?.latitude && fullAddressData.value?.longitude) {
      initialCoords = [fullAddressData.value.latitude, fullAddressData.value.longitude];
      consola.log('📍 updateMapForOrderType: Используем координаты из fullAddressData:', initialCoords);
    } else {
      consola.log('🗺️ updateMapForOrderType: Используем центр зоны доставки:', initialCoords);
    }

    // Маркер теперь реализован через CSS-оверлей, а не через Yandex Maps API
    // Это позволяет ему оставаться строго в центре карты

    // Обработчик клика по карте - центрируем карту на точке клика
    map.events.add('click', (e: any) => {
      const c = e.get('coords');
      map.setCenter(c, map.getZoom());
    });

    // Обработчик окончания действия с картой (перемещение, зум и т.д.)
    // CSS-маркер всегда остается в центре, карта двигается под ним
    map.events.add('actionend', () => {
      const center = map.getCenter();

      // Дебаунс для обработки координат (чтобы не спамить API)
      if (mapActionTimeout) {
        clearTimeout(mapActionTimeout);
      }
      mapActionTimeout = setTimeout(() => {
        handleCoords(center);
      }, MAP_CONFIG.DEBOUNCE_DELAY);
    });

    // Центрируем карту на адресе доставки
    map.setCenter(initialCoords, MAP_CONFIG.ADDRESS_ZOOM);
    consola.log('🗺️ Карта центрирована на адресе:', initialCoords);
  }

  // Всегда добавляем маркеры филиалов
  consola.log('🏪 Добавляем маркеры филиалов');
  createBranchMarkers();

  // Для режима самовывоза/зала центрируем карту на выбранном филиале
  if (isPickupOrDineInMode.value) {
    // Приоритет 1: Используем филиал из store
    let branchToSelect = institutionStore.selectedBranch;

    // Приоритет 2: Если филиал не выбран, но есть в локальном состоянии - используем его
    if (!branchToSelect && selectedBranch.value) {
      branchToSelect = selectedBranch.value;
      consola.log('🔄 Используем локально выбранный филиал:', branchToSelect?.name || 'неизвестно');
    }

    // Приоритет 3: Ищем филиал по адресу из формы
    if (!branchToSelect && form.address_institution) {
      const foundBranch = activeBranches.value.find(
        (b) => b.is_active !== false && b.latitude && b.longitude && b.address.street === form.address_institution,
      );
      if (foundBranch) {
        branchToSelect = foundBranch;
        consola.log('🔍 Найден филиал по адресу из формы:', branchToSelect.name);
      }
    }

    // Приоритет 4: Выбираем первый доступный
    if (!branchToSelect && activeBranches.value.length > 0) {
      branchToSelect = activeBranches.value[0];
      consola.log('🏪 Используем первый доступный филиал:', branchToSelect.name);
    }

    // Устанавливаем выбранный филиал
    if (branchToSelect && branchToSelect.latitude && branchToSelect.longitude) {
      selectedBranch.value = branchToSelect;
      institutionStore.setSelectedBranchId(branchToSelect.id);

      const coords = extractCoordinates(branchToSelect as any);
      if (areCoordinatesValid(coords)) {
        consola.log('🗺️ updateMapForOrderType: центрируем карту на филиале:', coords, branchToSelect.name);
        // Используем setTimeout для гарантии, что карта готова
        setTimeout(() => {
          if (map) {
            map.setCenter(coords, MAP_CONFIG.BRANCH_ZOOM);
            // Принудительно обновляем маркеры после центрирования
            updateBranchMarkersVisualState();
          }
        }, 100);
      } else {
        consola.warn('⚠️ Некорректные координаты филиала:', branchToSelect.name);
      }
    } else {
      consola.warn('⚠️ Не удалось найти филиал для центрирования карты');
    }
  }

  consola.log('✅ updateMapForOrderType: карта обновлена для режима:', localOrderType.value);
}

/* ---------- USER ADDRESSES FROM DB ---------- */
// Используем адреса из store userProfile (загруженные из API v2)
const userAddresses = computed(() => profileStore.getUserAddresses);

// Получаем дефолтный адрес (если есть)
const defaultAddress = computed(() => userAddresses.value.find((addr) => addr.isDefault && addr.isActive));

/**
 * ПРИОРИТЕТЫ ПРИ ИНИЦИАЛИЗАЦИИ КАРТЫ (для режима доставки):
 *
 * 1. 🏠 Режим редактирования (mode === 'edit')
 *    - Используются координаты редактируемого адреса
 *
 * 2. ⭐ Дефолтный адрес пользователя (ВСЕГДА ПРИОРИТЕТНЫЙ!)
 *    - Если есть адрес с isDefault: true - ВСЕГДА показывается первым
 *    - Независимо от того, есть ли адрес в форме или нет
 *    - Пользователь сам может изменить адрес через карту или инпут
 *
 * 3. 🏪 Первый филиал (если у пользователя нет адресов)
 *    - Если список адресов пуст - показываем координаты первого активного филиала
 *    - Помогает новым пользователям сориентироваться на карте
 *
 * 4. 🗺️ Центр зоны доставки
 *    - Используется если есть адреса, но нет дефолтного
 */

// Активные филиалы с координатами
const activeBranches = computed(() => {
  consola.log('🔍 activeBranches computed:', {
    totalBranches: institutionStore.branches.length,
    branches: institutionStore.branches.map((b) => ({
      id: b.id,
      name: b.name,
      is_active: b.is_active,
      latitude: b.latitude,
      longitude: b.longitude,
      // Детальная проверка каждого условия
      checks: {
        'is_active !== false': b.is_active !== false,
        'has latitude': !!b.latitude,
        'has longitude': !!b.longitude,
        'latitude type': typeof b.latitude,
        'longitude type': typeof b.longitude,
        'latitude value': b.latitude,
        'longitude value': b.longitude,
        'latitude as number': Number(b.latitude),
        'longitude as number': Number(b.longitude),
      },
    })),
  });

  const filtered = institutionStore.branches.filter((b: any) => {
    const isActive = b.is_active !== false;

    // Преобразуем координаты в числа и проверяем, что они валидные
    const lat = typeof b.latitude === 'string' ? Number.parseFloat(b.latitude) : b.latitude;
    const lng = typeof b.longitude === 'string' ? Number.parseFloat(b.longitude) : b.longitude;

    const hasLat = !Number.isNaN(lat) && lat !== null && lat !== undefined && lat !== 0;
    const hasLng = !Number.isNaN(lng) && lng !== null && lng !== undefined && lng !== 0;

    consola.log(`🔎 Проверка филиала ${b.name}:`, {
      id: b.id,
      isActive,
      original: { latitude: b.latitude, longitude: b.longitude },
      parsed: { lat, lng },
      hasLat,
      hasLng,
      passesFilter: isActive && hasLat && hasLng,
    });

    return isActive && hasLat && hasLng;
  });

  consola.log(
    '✅ activeBranches filtered:',
    filtered.length,
    filtered.map((b) => b.name),
  );

  return filtered;
});

/* ---------- MAP VARS ---------- */
const ymap = ref<HTMLElement | null>(null);
const selectedZoneId = ref(deliveryZones.value.length ? deliveryZones.value[0].id : null);
const selectedZone = computed(
  () => (institutionStore.institution as any)?.delivery_zones?.find((z: any) => z.id === selectedZoneId.value),
);
const selectedCoords = computed<[number, number]>(() => selectedZone.value?.center_zone_coords ?? [0, 0]);

let map: any = null;
let marker: any = null;
let polygons: any[] = [];
let branchMarkers: any[] = []; // Маркеры филиалов
let mapActionTimeout: any = null;

const currentZone = ref<any | null>(null);
const inPolygon = ref(false);
const selectedBranch = ref<any | null>(null);

// Флаг для отслеживания инициализации карты
const isMapInitialized = ref(false);

// Полные данные адреса для сохранения в БД
const fullAddressData = ref<{
  city: string;
  street: string;
  building: string;
  latitude: number;
  longitude: number;
} | null>(null);

// Дополнительные детали адреса (квартира/подъезд/этаж/домофон/комментарий)
const extraDetails = ref({
  apartment: '',
  entrance: '',
  floor: '',
  intercom: '',
  comment: '',
});
const showExtraDetails = ref(false);
/** Адрес, которому принадлежат уточнения (см. adoptAddressDetails). */
const detailsOwner = ref<AddressPlace | null>(null);

const extraDetailsSummary = computed(() => {
  const parts: string[] = [];
  if (extraDetails.value.apartment) parts.push(`кв. ${extraDetails.value.apartment}`);
  if (extraDetails.value.entrance) parts.push(`подъезд ${extraDetails.value.entrance}`);
  if (extraDetails.value.floor) parts.push(`этаж ${extraDetails.value.floor}`);
  if (extraDetails.value.intercom) parts.push(`домофон ${extraDetails.value.intercom}`);
  return parts.join(', ');
});

// Геолокация: предложение адреса из browser geolocation API
const geoSuggestion = ref<{
  address: string;
  coords: [number, number];
  data: { city: string; street: string; building: string };
  inZone: boolean;
} | null>(null);
const geoRequested = ref(false);
const geoLoading = ref(false);

// Проверка обязательных частей адреса по данным геокодера: населённый пункт + дом.
//
// Улица НЕ обязательна: в посёлках и хуторах её просто нет, дом нумеруется прямо
// по населённому пункту (в базе такие адреса живут нормально — например
// «96, хутор Осечки»). Требование улицы не давало таким пользователям сохранить
// собственный адрес, и его приходилось заводить вручную.
const missingAddressParts = computed<string[]>(() => {
  if (!isDeliveryMode.value) return [];
  if (!form.delivery_address?.address) return [];
  // Данные геокодера относятся к прежней точке, а не к набранной строке.
  if (isAddressUnconfirmed.value) return [];
  const data = fullAddressData.value;
  const missing: string[] = [];
  if (!data?.city?.trim()) missing.push('locality');
  if (!data?.building?.trim()) missing.push('building');
  return missing;
});

const ADDRESS_NOT_FOUND_MESSAGE =
  'Не нашли такой адрес. Передвиньте карту, чтобы маркер стоял на вашем доме.';

const isDeliverySaveDisabled = computed(() => {
  if (!form.delivery_address?.address?.trim()) return true;
  if (isSubmitting.value || addressNotFound.value) return true;
  // Набранный адрес ещё не найден на карте: зона сейчас от прежней точки, судить по ней
  // рано. Кнопка активна — «Сохранить» сначала найдёт адрес, потом проверит зону.
  if (isAddressUnconfirmed.value) return false;
  if (pointAtBranch.value) return true;
  return !inPolygon.value || !currentZone.value || missingAddressParts.value.length > 0;
});

const missingAddressPartsLabel = computed(() => {
  const labels: Record<string, string> = {
    locality: 'населённый пункт',
    street: 'улицу',
    building: 'номер дома',
  };
  return missingAddressParts.value.map((p) => labels[p]).filter(Boolean).join(', ');
});

/* ---------- HELPERS ---------- */
const toYaCoords = (coords: number[][]) => coords.map(([lat, lng]) => [lng, lat]);

// Константы для типов карт
const DARK_MAP_TYPE = 'dark#map';
const LIGHT_MAP_TYPE = 'yandex#map'; // Стандартная схема Яндекс.Карт

/**
 * Инициализирует кастомный тип карты для тёмной темы.
 * Использует официальные тайлы Яндекс.Карт с параметром theme=dark.
 */
const initializeDarkMapType = () => {
  if (!window.ymaps) {
    consola.warn('⚠️ ymaps не готов для инициализации тёмной темы');
    return;
  }

  consola.log('🔄 Инициализируем кастомный тип карты для тёмной темы');

  try {
    // Проверяем, не создан ли уже кастомный тип
    if (window.ymaps.mapType.storage.get(DARK_MAP_TYPE)) {
      consola.log('✅ Кастомный тип карты уже существует');
      return;
    }

    // Создаём кастомный слой для тёмной темы на официальных тайлах Яндекса (theme=dark)
    const DarkLayer = function () {
      return new window.ymaps.Layer('https://core-renderer-tiles.maps.yandex.net/tiles?l=map&theme=dark&%c&%l', {
        projection: window.ymaps.projection.wgs84Mercator,
        tileSize: [256, 256],
      });
    };

    window.ymaps.layer.storage.add(DARK_MAP_TYPE, DarkLayer);

    const darkMapType = new window.ymaps.MapType('Тёмная карта', [DARK_MAP_TYPE]);
    window.ymaps.mapType.storage.add(DARK_MAP_TYPE, darkMapType);

    consola.log('✅ Кастомный тип карты для тёмной темы инициализирован:', DARK_MAP_TYPE);
  } catch (error) {
    consola.error('❌ Ошибка при инициализации кастомного типа карты:', error);
  }
};

/**
 * Обновляет тему карты — переключает тип карты (светлый/тёмный)
 */
const updateMapTheme = () => {
  if (!map || !window.ymaps || !map.setType) {
    consola.warn('⚠️ Карта или ymaps не готовы для смены темы');
    return;
  }

  consola.log('🎨 Обновляем тему карты:', isDark.value ? 'dark' : 'light');

  try {
    const targetType = isDark.value ? DARK_MAP_TYPE : LIGHT_MAP_TYPE;

    // Тёмный тип может быть ещё не зарегистрирован — инициализируем по требованию
    if (isDark.value && !window.ymaps.mapType.storage.get(DARK_MAP_TYPE)) {
      consola.warn('⚠️ Тёмная карта не инициализирована, инициализируем...');
      initializeDarkMapType();
    }

    if (!window.ymaps.mapType.storage.get(targetType)) {
      consola.error('❌ Тип карты не найден:', targetType);
      return;
    }

    // Повторная проверка, что карта всё ещё существует
    if (!map || !map.setType) {
      consola.warn('⚠️ Карта была уничтожена до смены темы');
      return;
    }

    map.setType(targetType);
    consola.log('✅ Тема карты изменена на:', targetType);
  } catch (error) {
    consola.error('❌ Ошибка при смене темы карты:', error);
  }
};

/**
 * Парсит координату из строки или числа
 */
const parseCoordinate = (value: string | number): number => {
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : value;
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Извлекает координаты из объекта и возвращает массив [lat, lng]
 */
const extractCoordinates = (obj: { latitude: string | number; longitude: string | number }): [number, number] => [
  parseCoordinate(obj.latitude),
  parseCoordinate(obj.longitude),
];

/**
 * Проверяет валидность координат
 */
const areCoordinatesValid = (coords: [number, number]): boolean => {
  const [lat, lng] = coords;
  return !Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0;
};

/**
 * Создает маркеры для всех активных филиалов на карте
 */
function createBranchMarkers() {
  if (!map || !window.ymaps) {
    consola.warn('⚠️ createBranchMarkers: карта или ymaps не готовы');
    return;
  }

  // ДЕТАЛЬНАЯ ДИАГНОСТИКА
  consola.log('🔍 ДИАГНОСТИКА: institutionStore.branches:', {
    total: institutionStore.branches.length,
    branches: institutionStore.branches.map((b) => ({
      id: b.id,
      name: b.name,
      is_active: b.is_active,
      latitude: b.latitude,
      longitude: b.longitude,
      'typeof latitude': typeof b.latitude,
      'typeof longitude': typeof b.longitude,
    })),
  });

  consola.log('🏪 createBranchMarkers: начинаем создание маркеров', {
    activeBranchesCount: activeBranches.value.length,
    activeBranches: activeBranches.value.map((b) => ({ id: b.id, name: b.name })),
  });

  // Очищаем старые маркеры филиалов
  branchMarkers.forEach((marker) => map.geoObjects.remove(marker));
  branchMarkers = [];

  // Получаем логотип заведения (один на все филиалы)
  const logoUrl = (institutionStore.institution as any)?.visual?.logo_file || null;
  consola.log('🖼️ Логотип заведения:', logoUrl);

  // Создаем маркер для каждого филиала
  activeBranches.value.forEach((branch: any) => {
    const coords = extractCoordinates(branch);

    if (!areCoordinatesValid(coords)) {
      consola.warn(`Некорректные координаты для филиала ${branch.name}:`, coords);
      return;
    }

    const [lat, lng] = coords;

    // Проверяем, является ли этот филиал выбранным
    const isSelected = selectedBranch.value?.id === branch.id;
    const isSelectedClass = isSelected ? 'selected-branch-marker' : '';
    const styles = isSelected ? MARKER_STYLES.SELECTED : MARKER_STYLES.DEFAULT;

    consola.log(`🏪 Создаем маркер для филиала ${branch.name}:`, {
      isSelected,
      coordinates: [lat, lng],
      selectedBranchId: selectedBranch.value?.id,
      branchId: branch.id,
    });

    let branchMarker: any;

    if (logoUrl) {
      // Создаем кастомный HTML-макет с логотипом
      const customIconLayout = window.ymaps.templateLayoutFactory.createClass(
        `<div class="branch-marker ${isSelectedClass}" style="
          width: 48px;
          height: 48px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          border: ${styles.borderWidth} solid ${styles.borderColor};
          overflow: hidden;
          transform: ${styles.scale};
          transition: all 0.3s ease;
        ">
          <!--suppress HtmlDeprecatedAttribute -->
          <img
            src="${logoUrl}"
            alt="Logo"
            style="
              width: 46px;
              height: 46px;
              object-fit: contain;
              border-radius: 50%;
            "
            onerror="this.style.display='none'; this.parentElement.innerHTML='🍴';"
          />
        </div>`,
      );

      branchMarker = new window.ymaps.Placemark(
        [lat, lng],
        {
          balloonContentHeader: branch.name,
          balloonContentBody: `
            <div style="font-size: 14px; line-height: 1.5;">
              <strong>📍 ${branch.address.street}</strong><br>
              <span style="color: #666;">${branch.address.city}</span><br>
              📞 ${branch.phone}<br>
              🕒 ${branch.working_hours?.hours || 'Не указано'}
            </div>
          `,
          hintContent: branch.name,
        },
        {
          iconLayout: customIconLayout,
          iconShape: {
            type: 'Circle',
            coordinates: [24, 24],
            radius: 24,
          },
        },
      );
    } else {
      // Если логотипа нет, используем стандартную иконку ресторана
      branchMarker = new window.ymaps.Placemark(
        [lat, lng],
        {
          balloonContentHeader: branch.name,
          balloonContentBody: `
            <div style="font-size: 14px; line-height: 1.5;">
              <strong>📍 ${branch.address.street}</strong><br>
              <span style="color: #666;">${branch.address.city}</span><br>
              📞 ${branch.phone}<br>
              🕒 ${branch.working_hours?.hours || 'Не указано'}
            </div>
          `,
          hintContent: branch.name,
        },
        {
          preset: 'islands#redFoodIcon',
          iconColor: styles.borderColor,
          iconImageSize: isSelected ? [32, 32] : [28, 28],
        },
      );
    }

    // Добавляем обработчик клика на маркер
    branchMarker.events.add('click', () => {
      onSelectBranch(branch);
    });

    map.geoObjects.add(branchMarker);
    branchMarkers.push(branchMarker);
  });

  consola.log(`✅ Отображено ${branchMarkers.length} филиалов на карте`);
}

/**
 * Обновляет визуальное состояние маркеров филиалов без полной перерисовки
 */
function updateBranchMarkersVisualState() {
  if (!map || !window.ymaps) {
    consola.warn('❌ Карта или Yandex Maps не инициализированы');
    return;
  }

  consola.log('🔄 Обновляем визуальное состояние маркеров филиалов');

  // Обновляем только маркеры филиалов
  branchMarkers.forEach((marker) => {
    try {
      map.geoObjects.remove(marker);
    } catch (error) {
      consola.warn('⚠️ Ошибка при удалении маркера:', error);
    }
  });
  branchMarkers = [];

  // Пересоздаем маркеры с актуальным состоянием выбора
  createBranchMarkers();

  consola.log('✅ Маркеры филиалов обновлены');
}

async function resolveShortAddress(query: { coords?: [number, number]; searchStr?: string }) {
  const res = await window.ymaps.geocode(query.coords || query.searchStr);
  const geo = res.geoObjects.get(0);
  if (!geo) return { house: '', street: '', locality: '', precision: '', coords: null };

  const geocoderMeta = geo.properties?.get?.('metaDataProperty')?.GeocoderMetaData;
  const comps: any[] =
    typeof geo.getAddressComponents === 'function'
      ? geo.getAddressComponents()
      : geocoderMeta?.Address?.Components || [];

  const house = comps.find((c) => c.kind === 'house')?.name || '';
  // Улицы в посёлках и хуторах часто нет вовсе, и дом нумеруется по посёлку или кварталу:
  // Яндекс отдаёт их компонентом district («посёлок Южгипрониисельстрой», «квартал
  // Малышок»). Без него из строки пропадала самая нужная курьеру часть: «20, посёлок
  // Южгипрониисельстрой, станица Новотитаровская» превращалось в «20, станица
  // Новотитаровская». В городе district — это район, но там есть улица, и она важнее.
  const street =
    comps.find((c) => c.kind === 'street')?.name || comps.find((c) => c.kind === 'district')?.name || '';
  const loc =
    comps.find((c) => c.kind === 'locality' || c.kind === 'village' || c.kind === 'town' || c.kind === 'city')?.name ||
    '';
  // Точность ответа: exact/number — дом, street/other — улица или населённый пункт
  // (см. isHouseLevelResult). Раньше любой ответ с координатами считался домом.
  const precision: string = geocoderMeta?.precision || '';

  const coords = geo.geometry.getCoordinates();

  return { house, street, locality: loc, precision, coords };
}

function findZone(coords: [number, number]) {
  const found = polygons.find((p, i) => p.geometry.contains(coords) && deliveryZones.value[i]);
  const zoneIndex = polygons.findIndex((p) => p.geometry.contains(coords));
  return found ? deliveryZones.value[zoneIndex] : null;
}

/* ---------- WRITE TO STORE ---------- */
// coords кладём в выбранный адрес, а не только в checkoutStore.deliveryCoords:
// последний не переживает перезапуск приложения, и без координат бэкенд не даст
// оформить доставку (по ним подбирается филиал).
/**
 * @param options.persist записать точку и в выбранный адрес профиля (хранилище).
 * false — точка только показывается в окне (нет дома, маркер на филиале): такой
 * адрес не должен пережить закрытие окна и уехать в заказ.
 */
function writeAddress(
  addr: string,
  zona: string | null,
  coords?: [number, number] | null,
  options: { persist?: boolean } = {},
) {
  const obj = { address: addr, zona: zona ?? undefined };
  addressDraft.value = addr;
  form.delivery_address = obj;
  setField('delivery_address', obj);
  setField('delivery_zone_id', zona);
  if (options.persist !== false) {
    setSelectedDeliveryAddress({
      address: addr,
      zona: zona ? Number(zona) : null,
      latitude: coords ? coords[0] : null,
      longitude: coords ? coords[1] : null,
      ...currentAddressDetails(),
    });
  }
  addressPoint.confirmAddress(addr);
}

/**
 * Оформление: уточнения выбранного адреса (подъезд, квартира…) показываем в окне и
 * не теряем при пересохранении — они уходят в строку заказа. В режиме правки
 * сохранённого адреса детали берутся из него самого (см. initializeMap).
 */
function prefillDetailsFromSelectedAddress() {
  if (mode.value === 'edit') return;
  const selected = profileStore.selectedDeliveryAddress;
  if (!selected) return;
  extraDetails.value = {
    apartment: selected.apartment ?? '',
    entrance: selected.entrance ?? '',
    floor: selected.floor ?? '',
    intercom: selected.intercom ?? '',
    comment: selected.comment ?? '',
  };
  showExtraDetails.value = Boolean(extraDetailsSummary.value || extraDetails.value.comment);
}

/**
 * Сбрасывает уточнения: поля «Квартира», «Подъезд», «Этаж», «Домофон» и комментарий
 * курьеру принадлежат конкретному дому и на другой адрес молча переезжать не должны.
 */
function resetAddressDetails() {
  extraDetails.value = { apartment: '', entrance: '', floor: '', intercom: '', comment: '' };
}

/**
 * Запоминает, какому адресу принадлежат уточнения, и чистит их при переезде на другую
 * улицу или в другой населённый пункт.
 *
 * Зовётся там, где адрес уже РАЗОБРАН геокодером (точка на карте, выбор из «Моих
 * адресов»), а не на каждом нажатии клавиши: правка номера дома или опечатки уточнения
 * не трогает — иначе клиент, исправив одну букву, заново вбивает квартиру и подъезд.
 */
function adoptAddressDetails(place: AddressPlace) {
  if (detailsOwner.value && !isSameStreetAndLocality(detailsOwner.value, place)) {
    consola.debug('📍 Сменились улица или населённый пункт — уточнения адреса очищены');
    resetAddressDetails();
  }
  detailsOwner.value = { locality: place.locality ?? '', street: place.street ?? '' };
}

/** Уточнения из окна («Уточнить детали») — едут с выбранным адресом и попадают в строку заказа. */
function currentAddressDetails() {
  const d = extraDetails.value;
  return {
    entrance: d.entrance.trim() || null,
    apartment: d.apartment.trim() || null,
    floor: d.floor.trim() || null,
    intercom: d.intercom.trim() || null,
    comment: d.comment.trim() || null,
  };
}

/**
 * После закрытия окна строка в форме оформления должна совпадать с выбранным адресом —
 * именно его координаты уйдут в заказ. Набранный, но не найденный текст, закрытый крестиком,
 * иначе остался бы в форме и уехал в заказ по прежней точке.
 */
function syncFormWithSelectedAddress() {
  const selected = profileStore.selectedDeliveryAddress;
  if ((form.delivery_address?.address ?? '') === (selected?.address ?? '')) return;
  const zona = selected?.zona == null ? null : String(selected.zona);
  setField('delivery_address', selected ? { address: selected.address, zona: zona ?? undefined } : null);
  setField('delivery_zone_id', zona);
}

/** У строки адреса нет точки: зона и координаты прежней точки к ней не относятся. */
function clearAddressPoint() {
  currentZone.value = null;
  inPolygon.value = false;
  fullAddressData.value = null;
  pointAtBranch.value = null;
  // У адреса в окне точки больше нет — запасные координаты прежней точки (стор
  // оформления) к нему не относятся. Иначе заказ ушёл бы со строкой этого адреса
  // и координатами предыдущего: pickDeliveryCoords берёт их, когда у выбранного
  // адреса координат нет. Без них сервер подберёт точку по строке или попросит карту.
  checkoutStore.setDeliveryCoords(null);
}

/* ---------- MAIN HANDLER ---------- */
function handleCoords(coords: [number, number]): Promise<void> {
  return addressPoint.trackPoint(applyMapPoint(coords));
}

async function applyMapPoint(coords: [number, number]) {
  const request = addressPoint.beginPointRequest();
  try {
    const { house, street, locality, precision, coords: resolvedCoords } = await resolveShortAddress({ coords });
    if (request.isStale()) return;

    // Зону, строку и координаты пишем вместе и только после ответа геокодера: иначе
    // «Сохранить», нажатое в эту паузу, взяло бы зону новой точки с координатами старой.
    const zone = findZone(coords);
    currentZone.value = zone;
    inPolygon.value = !!zone;

    const point: [number, number] = resolvedCoords ?? coords;
    // Точка филиала — стартовая точка карты у клиента без адресов, а не его дом.
    const branchAtPoint =
      findBranchAtPoint(point[0], point[1], institutionStore.branches) ??
      findBranchAtPoint(coords[0], coords[1], institutionStore.branches);
    pointAtBranch.value = branchAtPoint ? { name: branchAtPoint.name } : null;

    // Единый формат точки: «населённый пункт, улица, дом» — одинаковый для ручного
    // ввода, маркера на карте и сохранённых адресов.
    const shortAddr = formatPointAddress({ locality, street, house });
    // Уехали на другую улицу или в другой населённый пункт — квартира, подъезд и
    // домофон от прежнего дома здесь не годятся.
    adoptAddressDetails({ locality, street });
    // В выбранный адрес и хранилище попадает только точка с домом и не на филиале:
    // остальное показываем в окне (строка, подсказка под полем), но адресом доставки
    // не делаем — иначе точка центра станицы или филиала пережила бы закрытие крестиком
    // и уехала в заказ (кластер из 5 адресов в центре Новотитаровской, заказ #8300).
    writeAddress(shortAddr, zone ? String(zone.id) : null, point, {
      persist: shouldPersistAddressPoint({
        house,
        precision,
        atBranch: !!branchAtPoint,
        inZone: !!zone,
      }),
    });

    // Сохраняем полные данные адреса для отправки в API
    fullAddressData.value = {
      city: locality,
      street,
      building: house,
      latitude: point[0],
      longitude: point[1],
    };
  } catch (error) {
    consola.error('❌ Ошибка при обработке координат:', error);
    if (!request.isStale()) clearAddressPoint();
  }
}

/* ---------- MANUAL INPUT ---------- */
function onManualAddressEnter(): Promise<void> {
  if (!map || !window.ymaps) return Promise.resolve();
  return addressPoint.lookupTypedAddress(findTypedAddress);
}

async function findTypedAddress(typed: string, isCurrent: () => boolean) {
  const request = addressPoint.beginPointRequest();
  // Пока геокодер отвечал, пользователь сдвинул карту, выбрал другой адрес, закрыл окно
  // или продолжил печатать — ответ относится к уже неактуальной строке.
  const isOutdated = () => request.isStale() || !map || !isCurrent();
  try {
    const found = await resolveShortAddress({ searchStr: typed });
    if (isOutdated()) return;
    // Принимаем только дом. Улица или населённый пункт («станица Новотитаровская»)
    // — это не адрес: раньше карта уезжала в центр станицы, обратный геокод
    // подставлял ближайший дом, и он сохранялся как адрес клиента.
    if (!found.coords || !isHouseLevelResult(found)) {
      markTypedAddressNotFound();
      return;
    }
    const coords = found.coords;
    // Карта переедет на найденный дом, handleCoords запишет его зону и адрес.
    map.setCenter(coords, MAP_CONFIG.ADDRESS_ZOOM);
    await handleCoords(coords);
  } catch (error) {
    consola.error('❌ Ошибка при геокодировании адреса:', error);
    if (!isOutdated()) markTypedAddressNotFound();
  }
}

function markTypedAddressNotFound() {
  clearAddressPoint();
  addressPoint.markAddressNotFound();
}

/**
 * Точка сохранённого адреса: координаты из БД, если они есть и это не координаты
 * филиала (след старого окна карты, #8300), иначе — дом по строке через геокодер.
 */
async function resolveSavedAddressPoint(item: any): Promise<[number, number] | null> {
  const hasOwnPoint =
    item.latitude && item.longitude && !isAtBranchPoint(item.latitude, item.longitude, institutionStore.branches);
  if (hasOwnPoint) return [item.latitude, item.longitude];
  if (!window.ymaps || !item.address) return null;
  // Принимаем только дом: улица или населённый пункт — не адрес доставки.
  const found = await resolveShortAddress({ searchStr: item.address });
  return found.coords && isHouseLevelResult(found) ? found.coords : null;
}

/** Зона строго по точке: старая zona адреса к точке вне зон не относится. */
function zonaForSavedAddress(item: any, coords: [number, number] | null): string | null {
  if (!coords) return item.zona ?? null;
  const zoneAtPoint = findZone(coords);
  return zoneAtPoint ? String(zoneAtPoint.id) : null;
}

/* ---------- PICK FROM LIST ---------- */
async function onSelectAddress(item: any) {
  // Выбор из списка отменяет незавершённый поиск точки: его ответ не должен перетереть адрес.
  addressPoint.beginPointRequest();
  try {
    const coords = await resolveSavedAddressPoint(item);
    pointAtBranch.value = null;
    const zona = zonaForSavedAddress(item, coords);

    // Записываем адрес в форму — в едином формате точки (строка старых адресов
    // пересобирается из компонентов, когда они согласованы со строкой). Выбранным
    // адресом он становится, только если его точка сейчас в зоне: у сохранённого
    // адреса зона могла измениться (границы правят в админке), и адрес с пустой зоной
    // потом молча подменяется адресом по умолчанию.
    writeAddress(canonicalSavedAddress(item), zona, coords, { persist: !!zona });

    // Заполняем полные данные адреса
    fullAddressData.value = {
      city: item.city || '',
      street: item.street || '',
      building: item.building || '',
      latitude: coords ? coords[0] : item.latitude || 0,
      longitude: coords ? coords[1] : item.longitude || 0,
    };

    // Подтягиваем дополнительные детали из сохранённого адреса
    extraDetails.value = {
      apartment: item.apartment || '',
      entrance: item.entrance || '',
      floor: item.floor == null ? '' : String(item.floor),
      intercom: item.intercom || '',
      comment: item.comment || '',
    };
    // Уточнения теперь принадлежат ЭТОМУ адресу: иначе переезд карты на его точку
    // (setCenter ниже) сравнил бы их с прежним адресом и тут же стёр.
    detailsOwner.value = { locality: item.city || '', street: item.street || '' };
    showExtraDetails.value = Boolean(extraDetailsSummary.value || extraDetails.value.comment);

    if (coords) {
      // Устанавливаем зону и флаг
      currentZone.value = findZone(coords);
      inPolygon.value = !!currentZone.value;

      // Запоминаем координаты выбранного адреса сразу: для доставки по ним сервер
      // подбирает филиал при оформлении (вкл. выбор из «Мои адреса», а не только
      // ручной ввод на карте).
      checkoutStore.setDeliveryCoords({ latitude: coords[0], longitude: coords[1] });

      // Перемещаем карту (маркер автоматически останется в центре через CSS)
      if (map && map.setCenter) {
        map.setCenter(coords, MAP_CONFIG.ADDRESS_ZOOM);
      }
    } else {
      // Сохранённый адрес не нашёлся на карте — зона прежней точки ему не принадлежит.
      clearAddressPoint();
    }
  } catch (error) {
    consola.error('❌ Ошибка при выборе адреса:', error);
  }
}

/* ---------- SELECT BRANCH ---------- */
function onSelectBranch(branch: any) {
  consola.log('🎯 Выбираем филиал:', {
    name: branch.name,
    address: branch.address,
    coordinates: [branch.latitude, branch.longitude],
    id: branch.id,
  });

  selectedBranch.value = branch;

  // Выбираем филиал по id — единый источник истины (важно при нескольких
  // филиалах в одном городе: по названию города выбор бы «залип» на первом).
  // chooseBranch: это ЯВНЫЙ выбор пользователя — сохраняем его на сутки,
  // чтобы обновление страницы не сбрасывало локацию.
  institutionStore.chooseBranch(branch.id);

  // Заполняем адрес заведения в форме
  setField('address_institution', branch.address.street);

  consola.log('✅ Филиал выбран и установлен:', {
    selectedBranch: selectedBranch.value?.name,
    selectedCity: institutionStore.selectedCity,
  });

  // Принудительно перерендериваем карту с новым филиалом по центру
  if (map && window.ymaps && isPickupOrDineInMode.value) {
    consola.log('🔄 Принудительно перерендериваем карту для выбранного филиала');

    // Сначала пробуем обычное обновление
    reinitializeMapForBranch(branch);

    // Если через 100мс карта все еще не обновилась, принудительно пересоздаем
    setTimeout(() => {
      if (map && selectedBranch.value?.id === branch.id) {
        const currentCenter = map.getCenter();
        const expectedCoords = extractCoordinates(branch);

        // Проверяем, центрирована ли карта на правильном филиале
        const distance = Math.sqrt(
          Math.pow(currentCenter[0] - expectedCoords[0], 2) + Math.pow(currentCenter[1] - expectedCoords[1], 2),
        );

        // Если расстояние больше threshold, пересоздаем карту
        if (distance > MAP_CONFIG.COORDINATE_THRESHOLD) {
          consola.log('⚠️ Карта не обновилась, принудительно пересоздаем');
          forceRecreateMapForBranch(branch);
        }
      }
    }, MAP_CONFIG.DEBOUNCE_DELAY);
  }
}

/**
 * Переинициализирует карту с выбранным филиалом по центру
 */
function reinitializeMapForBranch(branch: any) {
  if (!map || !window.ymaps || !map.setCenter) {
    consola.warn('⚠️ Карта не готова для переинициализации');
    return;
  }

  const coords = extractCoordinates(branch);

  if (!areCoordinatesValid(coords)) {
    consola.warn('❌ Некорректные координаты филиала:', coords);
    return;
  }
  consola.log('🗺️ Переинициализируем карту с координатами:', coords, branch.name);

  // Устанавливаем новый центр карты с небольшой задержкой для гарантии
  setTimeout(() => {
    if (map && map.setCenter) {
      map.setCenter(coords, MAP_CONFIG.BRANCH_ZOOM);

      // Пересоздаем все маркеры филиалов после центрирования
      setTimeout(() => {
        if (map) {
          updateBranchMarkersVisualState();
        }
      }, 50);
    }
  }, 50);

  consola.log('✅ Карта переинициализирована для филиала:', branch.name);
}

/**
 * Полностью пересоздает карту с новым филиалом (крайний случай)
 */
function forceRecreateMapForBranch(branch: any) {
  if (!ymap.value || !window.ymaps) {
    consola.warn('⚠️ Элемент DOM или ymaps не готовы для пересоздания карты');
    return;
  }

  consola.log('🔄 Принудительно пересоздаем карту для филиала:', branch.name);

  const coords = extractCoordinates(branch);

  if (!areCoordinatesValid(coords)) {
    consola.warn('❌ Некорректные координаты филиала:', coords);
    return;
  }

  try {
    // Уничтожаем старую карту
    if (map) {
      try {
        map.destroy();
      } catch (error) {
        consola.warn('⚠️ Ошибка при уничтожении карты:', error);
      }
      map = null;
      marker = null;
      branchMarkers = [];
      polygons = [];
    }

    // Создаем новую карту
    map = new window.ymaps.Map(ymap.value, {
      center: coords,
      zoom: MAP_CONFIG.BRANCH_ZOOM,
      controls: ['zoomControl'],
    });

    consola.log('✅ Карта пересоздана');

    // Создаем маркеры филиалов
    createBranchMarkers();

    consola.log('✅ Карта полностью пересоздана для филиала:', branch.name);
  } catch (error) {
    consola.error('❌ Ошибка при пересоздании карты:', error);
  }
}

/* ---------- GEOLOCATION SUGGESTION ---------- */
async function getCurrentCoords(): Promise<[number, number] | null> {
  // На нативе (iOS/Android) — используем Capacitor-плагин, иначе navigator.geolocation
  if (Capacitor.isNativePlatform()) {
    try {
      const permission = await Geolocation.checkPermissions();
      if (permission.location !== 'granted') {
        const requested = await Geolocation.requestPermissions({ permissions: ['location'] });
        if (requested.location !== 'granted') return null;
      }
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10_000 });
      return [pos.coords.latitude, pos.coords.longitude];
    } catch {
      return null;
    }
  }
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve([pos.coords.latitude, pos.coords.longitude]),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 600_000 },
    );
  });
}

async function requestGeolocation() {
  if (geoRequested.value) return;
  if (!isDeliveryMode.value) return;
  if (mode.value === 'edit') return;
  if (!window.ymaps) return;

  geoRequested.value = true;
  geoLoading.value = true;

  try {
    const coords = await getCurrentCoords();
    if (!coords) return;
    const { house, street, locality } = await resolveShortAddress({ coords });
    // Если нет дома или населённого пункта — не подсказываем, иначе валидация всё
    // равно не пройдёт. Улица необязательна: в посёлках её нет (см. missingAddressParts).
    if (!house || !locality) return;
    const address = formatPointAddress({ locality, street, house });
    const zone = findZone(coords);
    geoSuggestion.value = {
      address,
      coords,
      data: { city: locality, street, building: house },
      inZone: !!zone,
    };
  } catch {
    // Молча игнорируем — если геокодер не отдал адрес, просто не показываем плашку
  } finally {
    geoLoading.value = false;
  }
}

async function acceptGeoSuggestion() {
  if (!geoSuggestion.value) return;
  const { address, coords, data, inZone } = geoSuggestion.value;

  // Подставляем адрес в форму вместе с его точкой; незавершённый поиск точки отменяем
  addressPoint.beginPointRequest();
  pointAtBranch.value = null;
  form.delivery_address = { address };
  addressPoint.confirmAddress(address);
  fullAddressData.value = {
    ...data,
    latitude: coords[0],
    longitude: coords[1],
  };

  // Центрируем карту на найденной точке
  if (map && map.setCenter) {
    map.setCenter(coords, MAP_CONFIG.ADDRESS_ZOOM);
  }

  // Определяем зону
  currentZone.value = findZone(coords);
  inPolygon.value = !!currentZone.value;

  geoSuggestion.value = null;

  // Сразу сохраняем — если в зоне доставки
  if (inZone) {
    await submit();
  }
}

function rejectGeoSuggestion() {
  geoSuggestion.value = null;
}

/* ---------- SUBMIT ---------- */
async function submit() {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await submitSelection();
  } finally {
    isSubmitting.value = false;
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- разветвлённая отправка по типам заказа (delivery/takeaway/dine_in); рефактор требует прогона вживую
async function submitSelection() {
  // Разная логика для разных типов заказа
  if (isDeliveryMode.value) {
    // Валидация для режима доставки
    if (!form.delivery_address?.address || form.delivery_address.address.trim() === '') {
      alert('Адрес доставки не может быть пустым');
      return;
    }

    // Зона и координаты должны принадлежать той строке, что в поле. Набранный вручную
    // адрес сначала ищем, а ответы геокодера по карте дожидаемся.
    await onManualAddressEnter();
    await addressPoint.waitForPendingPoints();
    if (isAddressUnconfirmed.value) {
      alert(ADDRESS_NOT_FOUND_MESSAGE);
      return;
    }

    // Маркер на филиале — стартовая точка карты, а не дом клиента.
    if (pointAtBranch.value) {
      alert(`Маркер стоит на филиале «${pointAtBranch.value.name}» — передвиньте его на ваш дом.`);
      return;
    }

    // Проверка, что адрес в зоне доставки. Раньше проверки зоны: у найденного вручную
    // адреса вне зон нет и зоны, и «Выберите зону» не объясняло, в чём дело.
    if (!inPolygon.value) {
      alert('Указанный адрес находится вне зоны доставки');
      return;
    }

    // Валидация зоны
    if (!currentZone.value || !currentZone.value.id) {
      alert('Выберите зону доставки');
      return;
    }

    // Валидация полных данных адреса
    if (!fullAddressData.value) {
      alert('Не удалось определить полные данные адреса. Попробуйте выбрать адрес на карте.');
      return;
    }

    // Обязательны: населённый пункт, улица, дом
    if (missingAddressParts.value.length) {
      alert(`Уточните ${missingAddressPartsLabel.value} — передвиньте маркер на конкретное здание.`);
      return;
    }

    consola.log('✅ Адрес доставки прошел валидацию:', {
      address: form.delivery_address.address,
      zona: currentZone.value.id,
      mode: mode.value,
    });
  } else if (isPickupOrDineInMode.value) {
    // Валидация для режима самовывоза или "ем в зале"
    if (!selectedBranch.value) {
      alert('Выберите ресторан');
      return;
    }

    consola.log('✅ Филиал выбран:', {
      branch: selectedBranch.value.name,
      address: selectedBranch.value.address.street,
      mode: mode.value,
    });
  }

  // Режим работы с БД (создание/редактирование адреса) - только для доставки
  if (isDeliveryMode.value && (mode.value === 'create' || mode.value === 'edit')) {
    try {
      // Подготавливаем payload для API
      const floorNum = extraDetails.value.floor ? Number.parseInt(extraDetails.value.floor, 10) : Number.NaN;
      const payload: CreateAddressPayload | UpdateAddressPayload = {
        address: form.delivery_address?.address || '',
        zona: String(currentZone.value.id),
        city: fullAddressData.value?.city || '',
        street: fullAddressData.value?.street || '',
        building: fullAddressData.value?.building || '',
        latitude: fullAddressData.value?.latitude || 0,
        longitude: fullAddressData.value?.longitude || 0,
        apartment: extraDetails.value.apartment || undefined,
        entrance: extraDetails.value.entrance || undefined,
        floor: Number.isFinite(floorNum) ? floorNum : undefined,
        intercom: extraDetails.value.intercom || undefined,
        comment: extraDetails.value.comment || undefined,
        // Первый адрес устанавливаем как адрес по умолчанию
        isDefault: mode.value === 'create' && !hasAddresses.value,
      };

      if (mode.value === 'create') {
        // Создаем новый адрес
        consola.log('📝 Создание нового адреса в БД:', payload);
        const success = await createAddress(payload);

        if (success) {
          consola.log('✅ Адрес успешно создан в БД');
        } else {
          consola.error('❌ Ошибка создания адреса в БД');
          alert('Ошибка при сохранении адреса. Попробуйте еще раз.');
          return;
        }
      } else if (mode.value === 'edit') {
        // Обновляем существующий адрес
        const addressId = editingAddress.value?.id;
        if (!addressId) {
          consola.error('❌ ID адреса не найден для редактирования');
          alert('Ошибка: ID адреса не найден');
          return;
        }

        consola.log('📝 Обновление адреса в БД:', addressId, payload);
        const success = await updateAddress(addressId, payload as UpdateAddressPayload);

        if (success) {
          consola.log('✅ Адрес успешно обновлен в БД');
        } else {
          consola.error('❌ Ошибка обновления адреса в БД');
          alert('Ошибка при обновлении адреса. Попробуйте еще раз.');
          return;
        }
      }

      // После успешного сохранения в БД, обновляем локальный store
      setSelectedDeliveryAddress({
        address: form.delivery_address?.address || '',
        zona: currentZone.value.id ? Number(currentZone.value.id) : null,
        latitude: fullAddressData.value?.latitude ?? null,
        longitude: fullAddressData.value?.longitude ?? null,
      });
      setField('delivery_address', {
        address: form.delivery_address?.address || '',
        zona: currentZone.value.id ? String(currentZone.value.id) : undefined,
      });
      setField('delivery_zone_id', currentZone.value ? String(currentZone.value.id) : null);
    } catch (error) {
      consola.error('❌ Ошибка при сохранении адреса:', error);
      alert('Произошла ошибка при сохранении адреса');
      return;
    }
  } else if (isDeliveryMode.value) {
    // Режим checkout — доставка: обновляем локальный store
    if (modalStore.modalData?.from === 'profile') {
      const addressIdx = modalStore.modalData?.addressIdx ?? 0;
      profileStore.setDeliveryAddress(
        {
          address: form.delivery_address?.address || '',
          zona: currentZone.value.id ? String(currentZone.value.id) : null,
        },
        addressIdx,
      );
    } else {
      // Если не из профиля — только в стор! Подъезд и квартира едут вместе с адресом
      // и попадают в строку заказа (formatSelectedDeliveryAddress).
      setSelectedDeliveryAddress({
        address: form.delivery_address?.address || '',
        zona: currentZone.value.id ? Number(currentZone.value.id) : null,
        latitude: fullAddressData.value?.latitude ?? null,
        longitude: fullAddressData.value?.longitude ?? null,
        ...currentAddressDetails(),
      });
      setField('delivery_address', {
        address: form.delivery_address?.address || '',
        zona: currentZone.value.id ? String(currentZone.value.id) : undefined,
      });
      setField('delivery_zone_id', currentZone.value ? String(currentZone.value.id) : null);
    }
  } else if (isPickupOrDineInMode.value) {
    // Режим checkout — самовывоз или «ем в зале»
    setField('address_institution', selectedBranch.value.address.street);
    // chooseBranch, а не «уже сохранён через setSelectedBranchId»: @select срабатывает
    // только при СМЕНЕ радио, а кнопку подтверждения жмут и с предвыбранным филиалом —
    // без записи branchChoice такой явный выбор не переживал запуск, и стартовая
    // шторка спрашивала локацию при каждом старте. Вызов идемпотентен.
    institutionStore.chooseBranch(selectedBranch.value.id);
  }

  // ── Авто-роутинг доставки (Фаза 3) ──
  // Для доставки филиал выбирается автоматически по координатам адреса
  // (ближайший подходящий и не перегруженный; если все загружены — ближайший).
  // Клиент филиал не выбирает; подобранные филиал и зона становятся источником
  // для меню/цен/заказа. Окончательная защита от гонки — гард на бэке при создании.
  if (isDeliveryMode.value) {
    const lat = Number(fullAddressData.value?.latitude);
    const lng = Number(fullAddressData.value?.longitude);
    // Берём ID реально загруженного заведения: institutionStore.currentId — это
    // отдельный ref, который никто не синхронизирует с env (setInstitutionId не
    // вызывается нигде), поэтому он всегда дефолтит в 1, и резолв доставки уходил
    // на чужой институт → outOfZone на любом адресе. Источник истины — загруженный
    // institution.id (как в submit-ветке ниже, стр. ~1724).
    const instId = (institutionStore.institution as any)?.id || useRuntimeConfig().public.institutionID;
    if (Number.isFinite(lat) && Number.isFinite(lng) && instId) {
      // Запоминаем координаты сразу (до резолва): по ним сервер авторитетно подберёт
      // филиал при оформлении, даже если preview-резолв ниже не отработает.
      checkoutStore.setDeliveryCoords({ latitude: lat, longitude: lng });
      // Состав корзины: сервер подберёт только тот филиал, который делает ВЕСЬ заказ
      // (товар может быть эксклюзивом филиала — заказ не «прыгает» туда, где его нет).
      const productIds = cartStore.cart
        .filter((i: any) => i.itemType !== 'combo')
        .map((i: any) => String(i.productId ?? i.id));
      const comboIds = cartStore.cart
        .filter((i: any) => i.itemType === 'combo')
        .map((i: any) => String(i.comboId))
        .filter((id: string) => id && id !== 'undefined');
      try {
        const { data } = await apiInstitutions.resolveDeliveryBranch(instId, {
          latitude: lat,
          longitude: lng,
          orderSum: cartStore.totalPrice,
          productIds,
          comboIds,
        });
        if (data?.cartUnavailable) {
          alert(
            'Часть товаров из корзины не доставляется по этому адресу. ' +
              'Измените адрес или состав заказа.',
          );
          return;
        }
        if (data?.outOfZone || !data?.branchId) {
          alert('К сожалению, по этому адресу доставка пока недоступна.');
          return;
        }
        // chooseBranch: филиал подобран по адресу, который пользователь только что
        // явно подтвердил на карте, — это его выбор локации, сохраняем на сутки.
        institutionStore.chooseBranch(Number(data.branchId));
        if (data.zoneId) setField('delivery_zone_id', data.zoneId);
        consola.log('🚚 Авто-роутинг доставки:', {
          branchId: data.branchId,
          zoneId: data.zoneId,
          distanceKm: data.distanceKm,
          loaded: data.loaded,
          allLoaded: data.allLoaded,
        });
      } catch (error) {
        // Сбой резолва не блокирует оформление — остаёмся на текущем филиале/зоне.
        consola.error('❌ Авто-роутинг доставки не удался:', error);
      }
    }
  }

  consola.log('✅ Закрываем модалку с типом заказа:', localOrderType.value);
  consola.log('📋 Текущее состояние формы:', {
    method_receipt: form.method_receipt,
    delivery_address: form.delivery_address?.address,
    address_institution: form.address_institution,
  });

  modalStore.close();
}

/* ---------- ERROR MESSAGE ---------- */
const deliveryAddressErrorMsg = computed(() => {
  if (!form.delivery_address?.address) return '';
  if (addressNotFound.value) return 'Адрес не найден — укажите дом на карте';
  // Пока набранный адрес не найден, зона и данные геокодера — от прежней точки.
  if (isAddressUnconfirmed.value) return '';
  if (pointAtBranch.value) return 'Маркер стоит на филиале — передвиньте его на ваш дом';
  // Адрес остаётся в поле как есть: подменять его на другой нельзя — клиент не заметит
  // подмены и увезёт заказ в чужое место. Пусть выбирает сам.
  if (!inPolygon.value) return 'Сюда доставки нет — укажите другой адрес';
  if (missingAddressParts.value.length) return `Уточните ${missingAddressPartsLabel.value}`;
  if (currentZone.value && cartStore.totalPrice < Number(currentZone.value.min_order_limit))
    return `Минимальная сумма для этой зоны: ${currentZone.value.min_order_limit} ₽`;
  return '';
});

/* ---------- WATCHERS ---------- */
// Отслеживаем изменения локального типа заказа
watch(localOrderType, (newType) => {
  const newIndex = tabs.findIndex((tab) => tab.value === newType);
  if (newIndex >= 0 && newIndex !== selectedTabIndex.value) {
    selectedTabIndex.value = newIndex;
  }
});

// Отслеживаем изменения типа заказа из формы (для синхронизации)
watch(
  () => form.method_receipt,
  (newType) => {
    if (newType && newType !== localOrderType.value) {
      localOrderType.value = newType;
    }
  },
);

// Отслеживаем изменения выбранного филиала для обновления маркеров
watch(
  selectedBranch,
  (newBranch, oldBranch) => {
    if (newBranch !== oldBranch && map && window.ymaps) {
      // Для режима самовывоза/зала принудительно перерендериваем карту
      if (isPickupOrDineInMode.value && newBranch) {
        reinitializeMapForBranch(newBranch);
      } else {
        // Для режима доставки просто обновляем маркеры
        updateBranchMarkersVisualState();
      }
    }
  },
  { deep: true },
);

// Отслеживаем изменения выбранного филиала в institution store
watch(
  () => institutionStore.selectedBranch,
  (newBranch) => {
    if (newBranch && newBranch !== selectedBranch.value) {
      selectedBranch.value = newBranch;
    }
  },
  { deep: true },
);

// Отслеживаем открытие модального окна для инициализации карты
watch(
  () => modalStore.isOpen,
  async (isOpen) => {
    consola.log('🔄 Модалка изменила состояние:', isOpen ? 'открыта' : 'закрыта');

    if (isOpen && !isMapInitialized.value) {
      consola.log('✅ Модалка открылась, запускаем инициализацию карты');
      // Черновик поля берём заново при каждом открытии: пока окно было закрыто, адрес
      // могли сменить в «Моих адресах» или в шторке филиала.
      addressDraft.value = profileStore.selectedDeliveryAddress?.address ?? form.delivery_address?.address ?? '';
      // Даем время на рендер DOM
      await nextTick();
      await initializeMap();
    } else if (!isOpen && isMapInitialized.value) {
      consola.log('🔒 Модалка закрылась, сбрасываем флаг инициализации');
      isMapInitialized.value = false;
      // Сбрасываем состояние геолокации, чтобы при следующем открытии модалки запросить заново
      geoRequested.value = false;
      geoSuggestion.value = null;
    }
  },
  { immediate: false },
);

// Отслеживаем изменения темы приложения
watch(isDark, (newTheme) => {
  consola.log('🎨 Изменение темы приложения:', newTheme ? 'dark' : 'light');

  // Проверяем, что карта уже создана и не уничтожена
  if (map && window.ymaps && map.setType) {
    // Добавляем небольшую задержку для корректной работы
    setTimeout(() => {
      // Повторная проверка перед обновлением (карта могла быть уничтожена за время задержки)
      if (map && map.setType) {
        updateMapTheme();
      }
    }, 100);
  } else {
    consola.warn('⚠️ Карта еще не создана или уже уничтожена, тема будет применена при создании');
  }
});

/* ---------- RESIZE HANDLER ---------- */
// Обработчик изменения размера окна для перерисовки карты
let resizeTimeout: NodeJS.Timeout | null = null;
// Наблюдатель за размером контейнера карты — перерисовываем при изменении габаритов МОДАЛКИ (не только окна)
let resizeObserver: ResizeObserver | null = null;

const handleResize = () => {
  if (!map || !window.ymaps) return;

  // Дебаунс для предотвращения множественных вызовов
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }

  resizeTimeout = setTimeout(() => {
    // Повторная проверка, т.к. за время задержки карта могла быть уничтожена
    if (!map || !map.container) {
      consola.warn('⚠️ Карта уже уничтожена, пропускаем resize');
      return;
    }

    try {
      consola.log('🔄 Изменение размера окна: перерисовываем карту');
      // Вызываем метод container.fitToViewport() для корректной перерисовки
      map.container.fitToViewport();
    } catch (error) {
      consola.error('❌ Ошибка при перерисовке карты:', error);
    }
  }, 300); // Задержка 300мс
};

/**
 * Функция инициализации карты - вызывается когда модалка открыта и DOM готов
 */
/**
 * Инициализация идёт ровно одна за раз.
 *
 * Окно открывается — и `onMounted`, и вотчер на `modalStore.isOpen` зовут инициализацию
 * почти одновременно. Флага `isMapInitialized` для этого мало: он поднимается в самом
 * конце, поэтому оба вызова проходили проверку, делили между собой пять попыток
 * дождаться DOM-элемента, и второй заканчивался «Элемент карты не найден после всех
 * попыток» — окно оставалось с пустой заглушкой вместо карты. Ловилось не всегда:
 * гонка зависит от того, кто успел раньше.
 */
let mapInitPromise: Promise<void> | null = null;

function initializeMap(): Promise<void> {
  if (mapInitPromise) {
    consola.log('⏭️ Инициализация карты уже идёт — ждём её, а не запускаем вторую');
    return mapInitPromise;
  }
  mapInitPromise = runMapInitialization().finally(() => {
    mapInitPromise = null;
  });
  return mapInitPromise;
}

async function runMapInitialization() {
  if (isMapInitialized.value) {
    consola.log('⏭️ Пропускаем инициализацию карты: уже инициализирована');
    return;
  }

  consola.log('🗺️ Начинаем инициализацию карты...');

  // Ждем, пока DOM полностью отрендерится
  await nextTick();

  // Проверяем, что элемент карты существует (с несколькими попытками)
  let attempts = 0;
  const maxAttempts = 5;

  while (!ymap.value && attempts < maxAttempts) {
    consola.log(`⏳ Ожидание элемента карты, попытка ${attempts + 1}/${maxAttempts}...`);
    await new Promise((resolve) => setTimeout(resolve, 100));
    attempts++;
  }

  if (!ymap.value) {
    consola.error('❌ Элемент карты не найден после всех попыток, отменяем инициализацию');
    return;
  }

  consola.log('✅ Элемент карты найден, продолжаем инициализацию');

  // Догружаем API карт по требованию: глобально в <head> он больше не висит,
  // иначе каждая страница сайта тащила бы сотни килобайт стороннего JS.
  try {
    await ensureYandexMaps();
  } catch (error) {
    consola.error('❌ Yandex Maps API не загрузился:', error);
    return;
  }

  // Проверяем и загружаем филиалы заведения
  consola.log('🔄 Проверяем филиалы в store:', {
    branchesCount: institutionStore.branches.length,
    branches: institutionStore.branches,
    institution: institutionStore.institution,
    currentId: institutionStore.currentId,
  });

  // ВРЕМЕННО для диагностики: всегда перезагружаем филиалы
  const instId = (institutionStore.institution as any)?.id || useRuntimeConfig().public.institutionID;
  consola.log('🔄 ДИАГНОСТИКА: Принудительно перезагружаем филиалы для ID:', instId);

  try {
    await institutionStore.fetchBranches(instId);
    consola.log('✅ Филиалы перезагружены:', institutionStore.branches.length, institutionStore.branches);
  } catch (error) {
    consola.error('❌ Ошибка загрузки филиалов:', error);
  }

  // Загружаем адреса пользователя из API v2
  consola.log('🔄 Загружаем адреса пользователя из store...');
  await profileStore.fetchUserAddresses();
  consola.log('✅ Адреса загружены из store:', profileStore.userAddresses.length);
  consola.log('📍 Дефолтный адрес:', defaultAddress.value?.address || 'отсутствует');

  // Инициализация данных для режима редактирования
  prefillDetailsFromSelectedAddress();

  if (mode.value === 'edit' && editingAddress.value) {
    consola.log('📝 Режим редактирования, загружаем данные адреса:', editingAddress.value);

    // Заполняем форму данными редактируемого адреса
    form.delivery_address = {
      address: editingAddress.value.address,
      zona: editingAddress.value.zona || undefined,
    };
    addressPoint.confirmAddress(editingAddress.value.address);

    // Заполняем fullAddressData
    fullAddressData.value = {
      city: editingAddress.value.city || '',
      street: editingAddress.value.street || '',
      building: editingAddress.value.building || '',
      latitude: editingAddress.value.latitude || 0,
      longitude: editingAddress.value.longitude || 0,
    };

    // Подтягиваем дополнительные детали из редактируемого адреса
    extraDetails.value = {
      apartment: editingAddress.value.apartment || '',
      entrance: editingAddress.value.entrance || '',
      floor: editingAddress.value.floor == null ? '' : String(editingAddress.value.floor),
      intercom: editingAddress.value.intercom || '',
      comment: editingAddress.value.comment || '',
    };
    showExtraDetails.value = Boolean(extraDetailsSummary.value || extraDetails.value.comment);

    // Устанавливаем зону
    currentZone.value = deliveryZones.value.find((z: any) => String(z.id) === editingAddress.value.zona) || null;
    inPolygon.value = !!currentZone.value;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity -- инициализация Яндекс-карты (создание/центрирование/обработчики); рефактор требует прогона вживую
  window.ymaps.ready(() => {
    // Проверяем, что элемент DOM существует
    if (!ymap.value) {
      consola.error('❌ Элемент карты не найден в DOM');
      return;
    }

    // Определяем начальные координаты карты
    let initialCoords = selectedCoords.value;

    // Приоритет 1: Режим редактирования - используем координаты редактируемого адреса
    if (mode.value === 'edit' && editingAddress.value?.latitude && editingAddress.value?.longitude) {
      initialCoords = [editingAddress.value.latitude, editingAddress.value.longitude];
      consola.log('🏠 Режим редактирования: используем координаты редактируемого адреса:', initialCoords);
    }
    // Приоритет 2: ВСЕГДА показываем дефолтный адрес (если он есть и это режим доставки)
    else if (isDeliveryMode.value && defaultAddress.value?.latitude && defaultAddress.value?.longitude) {
      initialCoords = [defaultAddress.value.latitude, defaultAddress.value.longitude];
      consola.log('⭐ Инициализация: ВСЕГДА используем дефолтный адрес:', initialCoords, defaultAddress.value.address);
    }
    // Приоритет 3: Если у пользователя НЕТ адресов вообще - показываем первый филиал
    else if (isDeliveryMode.value && userAddresses.value.length === 0 && activeBranches.value.length > 0) {
      const firstBranch = activeBranches.value[0];
      if (firstBranch.latitude && firstBranch.longitude) {
        initialCoords = extractCoordinates(firstBranch as any);
        consola.log(
          '🏪 Инициализация: У пользователя нет адресов, показываем первый филиал:',
          initialCoords,
          firstBranch.name,
        );
      }
    } else if (isDeliveryMode.value) {
      consola.log('🗺️ Инициализация: дефолтного адреса нет, используем центр зоны:', initialCoords);
    }

    // Инициализируем кастомную тёмную карту перед созданием
    initializeDarkMapType();

    // Определяем тип карты в зависимости от темы приложения
    const mapType = isDark.value ? DARK_MAP_TYPE : LIGHT_MAP_TYPE;

    try {
      // Создаем карту с нужным типом
      map = new window.ymaps.Map(
        ymap.value,
        {
          center: initialCoords,
          zoom: MAP_CONFIG.DEFAULT_ZOOM,
          controls: [], // Убрали zoomControl для скрытия кнопок навигации
          type: mapType,
        },
        {
          // Скрываем все ссылки и элементы управления
          suppressMapOpenBlock: true, // Скрывает ссылку "Открыть в Яндекс.Картах"
          yandexMapDisablePoiInteractivity: true, // Отключает взаимодействие с POI
          copyrightProvidersVisible: false, // Скрывает копирайты провайдеров
          copyrightUaVisible: false, // Скрывает пользовательское соглашение
          copyrightLogoVisible: false, // Скрывает логотип
        },
      );

      consola.log('✅ Карта создана с типом:', mapType);
    } catch (error) {
      consola.error('❌ Ошибка создания карты:', error);
      return;
    }

    // Инициализируем карту в зависимости от типа заказа
    updateMapForOrderType();

    // Инициализируем обработку координат только для доставки и если не в режиме редактирования
    if (isDeliveryMode.value && mode.value !== 'edit') {
      // ВСЕГДА подставляем дефолтный адрес (если он есть), независимо от того, есть ли адрес в форме
      if (defaultAddress.value?.latitude && defaultAddress.value?.longitude) {
        consola.log('⭐ Инициализация: ВСЕГДА подставляем дефолтный адрес:', defaultAddress.value.address);
        // Используем существующую функцию для выбора адреса
        onSelectAddress(defaultAddress.value);
        return;
      }

      // Если у пользователя НЕТ адресов вообще - НЕ обрабатываем координаты (маркер уже на филиале)
      if (userAddresses.value.length === 0) {
        consola.log('🏪 Инициализация: у пользователя нет адресов, маркер установлен на филиал');
        // Обрабатываем координаты филиала для определения зоны
        handleCoords(initialCoords);
        return;
      }

      // Если дефолтного адреса нет, но есть другие адреса - используем координаты по умолчанию
      consola.log('🗺️ Инициализация: дефолтного адреса нет, обрабатываем координаты центра зоны');
      handleCoords(initialCoords);
    } else if (isPickupOrDineInMode.value) {
      // Для самовывоза и "ем в зале" - если есть выбранный филиал, центрируем карту на нем
      consola.log('🔍 Инициализация для самовывоза/зала:', {
        selectedBranch: institutionStore.selectedBranch,
        branches: institutionStore.branches.length,
        activeBranches: activeBranches.value.length,
        formAddressInstitution: form.address_institution,
      });

      // Приоритет 1: Филиал из формы (если есть address_institution)
      let branchToSelect: any = null;

      if (form.address_institution) {
        const foundBranch = activeBranches.value.find((b) => b.address.street === form.address_institution);
        if (foundBranch) {
          branchToSelect = foundBranch;
          consola.log('🔍 Ищем филиал по адресу из формы:', form.address_institution, branchToSelect.name);
        }
      }

      // Приоритет 2: Выбранный филиал из store
      if (!branchToSelect && institutionStore.selectedBranch?.latitude && institutionStore.selectedBranch?.longitude) {
        branchToSelect = institutionStore.selectedBranch;
        consola.log('🔍 Используем филиал из store:', branchToSelect.name);
      }

      // Приоритет 3: Первый доступный филиал
      if (!branchToSelect && activeBranches.value.length > 0) {
        branchToSelect = activeBranches.value[0];
        consola.log('🔍 Используем первый доступный филиал:', branchToSelect.name);
      }

      if (branchToSelect && branchToSelect.latitude && branchToSelect.longitude) {
        selectedBranch.value = branchToSelect;
        institutionStore.setSelectedBranchId(branchToSelect.id);

        const branchCoords = extractCoordinates(branchToSelect as any);
        if (areCoordinatesValid(branchCoords)) {
          // Используем setTimeout для гарантии, что карта полностью инициализирована
          setTimeout(() => {
            if (map) {
              map.setCenter(branchCoords, MAP_CONFIG.BRANCH_ZOOM);
              consola.log('📍 Установлен филиал при инициализации:', branchToSelect.name, 'координаты:', branchCoords);

              // Принудительно обновляем маркеры после центрирования
              setTimeout(() => {
                updateBranchMarkersVisualState();
              }, 100);
            }
          }, 200);
        } else {
          consola.warn('⚠️ Некорректные координаты филиала:', branchToSelect.name);
        }
      } else {
        consola.warn('⚠️ Не удалось найти филиал для инициализации');
      }
    }

    // Отмечаем, что карта успешно инициализирована
    isMapInitialized.value = true;
    consola.log('✅ Карта полностью инициализирована');

    // Запрашиваем геолокацию пользователя, чтобы предложить адрес
    requestGeolocation();
  });
}

/* ---------- LIFECYCLE HOOKS ---------- */
onMounted(async () => {
  emit('update:size', 'l');

  consola.log('🔄 ModalMapYandex onMounted (ClientOnly компонент)');

  // Добавляем обработчик изменения размера окна
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize);

    // Перерисовываем карту при изменении размеров её контейнера (ресайз модалки/смена брейкпоинта),
    // а не только при ресайзе окна — иначе карта остаётся в старых габаритах.
    if ('ResizeObserver' in window && ymap.value) {
      resizeObserver = new ResizeObserver(() => handleResize());
      resizeObserver.observe(ymap.value);
    }
  }

  // Инициализируем карту (она инициализируется только когда модалка открыта)
  // Проверяем, открыта ли модалка
  if (modalStore.isOpen) {
    consola.log('✅ Модалка открыта, запускаем инициализацию карты');
    await initializeMap();
  } else {
    consola.log('⏭️ Модалка закрыта, ожидаем открытия');
  }
});

// Очистка таймаута и маркеров при размонтировании
onUnmounted(() => {
  consola.log('🧹 Размонтирование ModalMapYandex: очистка ресурсов');

  syncFormWithSelectedAddress();

  // Удаляем обработчик изменения размера
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize);
  }

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  // Очищаем таймауты
  if (mapActionTimeout) {
    clearTimeout(mapActionTimeout);
    mapActionTimeout = null;
  }

  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
    resizeTimeout = null;
  }

  // Очищаем маркеры и полигоны
  if (map && window.ymaps) {
    try {
      // Удаляем все обработчики событий карты
      if (map.events && typeof map.events.removeAll === 'function') {
        map.events.removeAll();
      }

      // Очищаем маркеры филиалов
      if (branchMarkers.length > 0) {
        branchMarkers.forEach((marker) => {
          try {
            if (marker && map.geoObjects) {
              map.geoObjects.remove(marker);
            }
          } catch (error) {
            consola.warn('⚠️ Ошибка при удалении маркера филиала:', error);
          }
        });
        branchMarkers = [];
      }

      // Очищаем полигоны
      if (polygons.length > 0) {
        polygons.forEach((poly) => {
          try {
            if (poly && map.geoObjects) {
              map.geoObjects.remove(poly);
            }
          } catch (error) {
            consola.warn('⚠️ Ошибка при удалении полигона:', error);
          }
        });
        polygons = [];
      }

      // Очищаем маркер адреса
      if (marker) {
        try {
          map.geoObjects.remove(marker);
        } catch (error) {
          consola.warn('⚠️ Ошибка при удалении маркера адреса:', error);
        }
        marker = null;
      }

      // Уничтожаем карту
      if (map.destroy && typeof map.destroy === 'function') {
        map.destroy();
      }
      map = null;

      consola.log('✅ Карта и все ресурсы успешно очищены');
    } catch (error) {
      consola.error('❌ Ошибка при очистке карты:', error);
    }
  }

  // Сбрасываем флаг инициализации
  isMapInitialized.value = false;

  consola.log('✅ ModalMapYandex размонтирован');
});

/* ---------- (Опционально) REACTIVE INPUT ----------- */
// Чтобы карта реагировала на прямой ввод в input (с debounce!):
// import { debounced } from '~/utils/debounce'
// watch(() => form.delivery_address?.address, debounced((val, oldVal) => {
//   if (val && val !== oldVal) {
//     onManualAddressEnter()
//   }
// }, 600))
</script>

<style lang="scss">
.delivery-modal {
  @apply p-2.5  bg-white dark:bg-gray-900 lg:w-[992px];

  &__grid {
    @apply grid lg:gap-5 lg:grid-cols-[1.2fr_2fr] content-center lg:gap-10 bg-sky-300 dark:bg-zinc-900 rounded-2xl pb-4
    lg:py-8 lg:pr-8  lg:pl-10 max-lg:min-h-[calc(100dvh-20px)] gap-4 lg:pt-8 max-lg:grid-rows-[minmax(auto,70dvh)_1fr] overflow-hidden;
    // Десктоп: фикс высоты модалки, строка тянется (content-stretch) — панель и карта во всю высоту
    @apply lg:h-[78vh] lg:content-stretch;
  }

  &__panel {
    @apply order-2 lg:order-1  px-6 lg:px-0 flex flex-col;
    @apply lg:h-full lg:min-h-0 lg:overflow-hidden;
  }

  &__address {
    @apply mb-[18px];

    label {
      @apply text-[18px] font-semibold;
    }
  }

  &__map {
    @apply order-1 lg:order-2 flex-1 min-w-0 relative h-full z-10;
  }
}

// Десктоп: вкладка «Доставка» — колонка во всю высоту панели.
// Скроллится ВЕСЬ левый блок (адрес + «Уточнить детали» + «Мои адреса»),
// кнопка «Сохранить» всегда прижата к низу модалки.
.delivery-tab {
  @apply lg:flex lg:flex-col lg:h-full lg:min-h-0;

  &__scroll {
    @apply lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1;
  }

  &__save {
    @apply lg:flex-shrink-0 lg:pt-3;
  }
}

.geo-suggest {
  @apply mt-3 mb-2 p-3 rounded-xl border border-orange-300 dark:border-orange-500/40
         bg-orange-50 dark:bg-orange-500/10;

  &__title {
    @apply flex items-center gap-2 font-semibold text-gray-900 dark:text-white text-sm;
  }

  &__addr {
    @apply mt-2 text-sm text-gray-700 dark:text-gray-200;
  }

  &__warn {
    @apply mt-2 text-xs text-orange-700 dark:text-orange-300;
  }

  &__actions {
    @apply mt-3 flex items-center gap-2 flex-wrap;
  }

  &__no {
    @apply text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white
           underline-offset-2 hover:underline cursor-pointer;
  }
}

.address-details {
  @apply mt-3 mb-4;

  &__toggle {
    @apply flex items-center gap-2.5 w-full text-left px-3 py-2.5 rounded-hui-md cursor-pointer;
    @apply bg-hui-default-100 hover:bg-hui-default-200 transition-colors;
    @apply text-sm text-hui-foreground;
  }

  &__toggle-icon {
    @apply flex items-center justify-center w-7 h-7 rounded-full shrink-0;
    @apply bg-hui-content1 text-hui-foreground/70;
  }

  &__toggle-label {
    @apply flex-1 font-semibold;
  }

  &__summary {
    @apply text-xs text-hui-foreground/50 font-normal truncate max-w-[45%];
  }

  &__chevron {
    @apply text-hui-foreground/50 transition-transform duration-200 shrink-0;
  }

  &--open &__chevron {
    @apply rotate-180;
  }

  &__grid {
    @apply grid grid-cols-2 gap-3 mt-3 p-3 rounded-hui-md;
    @apply bg-hui-content1 border border-hui-divider;
  }
}

// Анимация раскрытия деталей адреса
.address-details-expand-enter-active,
.address-details-expand-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.address-details-expand-enter-from,
.address-details-expand-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

// Обертка для карты и маркера
.map-wrapper {
  @apply order-1 lg:order-2 flex-1 min-w-0 relative;
  position: relative;
}

// Селектор способа получения поверх карты.
// Мобайл: под крестиком на всю ширину (рядом не помещается — ломалась вёрстка).
// Десктоп: рядом с крестиком сверху (места хватает).
.map-tabs {
  position: absolute;
  left: 12px;
  right: 12px;
  top: calc(4.5rem + var(--safe-area-inset-top, 0px)); // под крестиком (top 1.25rem + высота 3rem)
  z-index: 30;

  @media (min-width: 1024px) {
    // По центру карты (крестик отдельно справа сверху)
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    top: 1.375rem;
  }

  [role='tablist'] {
    @apply border border-hui-divider shadow-lg;
  }
}

// Фиксированный маркер в центре карты
.center-marker {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -100%);
  z-index: 1000;
  pointer-events: none;

  img {
    width: 72px;
    height: 72px;
    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3));
  }
}

// Скрываем только ссылки и копирайты Яндекс.Карт (не саму карту!)
.delivery-modal__map {
  :deep(.ymaps-2-1-79-map-copyrights-promo) {
    display: none !important;
  }

  :deep(.ymaps-2-1-79-copyright) {
    display: none !important;
  }

  :deep(.ymaps-2-1-79-copyright__link) {
    display: none !important;
  }

  :deep(.ymaps-2-1-79-copyright__wrap) {
    display: none !important;
  }

  // Скрываем ссылки "Открыть в Яндекс.Картах"
  :deep(a[href*='maps.yandex']) {
    display: none !important;
  }
}

.my-address {
  @apply my-3 mb-4 border-b border-[#eee] pb-2.5;

  &__item {
    @apply py-1 text-[15px] text-[#222] flex items-start gap-[7px];

    input[type='radio'] {
      @apply accent-[#ffe17f] mt-[2px];
    }
  }

  &__add-btn {
    @apply mt-2.5 bg-[#f4f4f4] border-none rounded-lg py-2 w-full text-[16px] font-medium text-[#222] cursor-pointer transition-colors duration-100 hover:bg-[#ffe17f] hover:text-black;
  }
}

// Стили для маркеров филиалов на карте
.branch-marker {
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  &.selected-branch-marker {
    animation: pulse 2s infinite;
    box-shadow: 0 0 0 4px rgba(39, 174, 96, 0.3);
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(39, 174, 96, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(39, 174, 96, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(39, 174, 96, 0);
  }
}
</style>
